// @flow

import React, { PureComponent, Suspense } from 'react';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { Translation } from 'react-i18next';
import { graphql, QueryRenderer, fetchQuery } from 'react-relay';
import styled, { keyframes } from 'styled-components';
import relayEnvironment from 'shared/gql/relayEnvironment';
import AlertNotifications from 'shared/components/common/AlertNotifications';
import { setCompanyCurrency } from 'shared/utils';
import AiChat from './components/AiChat';
import config from './config';
import initAnalytics from './initAnalytics';

// ---------------------------------------------------------------------------
// Idle timeout constants
// ---------------------------------------------------------------------------
const IDLE_TIMEOUT_MS = 15 * 60 * 1000;   // 15 minutes → force logout
const WARN_BEFORE_MS  =  1 * 60 * 1000;   // warn 1 minute before logout
const WARN_AT_MS = IDLE_TIMEOUT_MS - WARN_BEFORE_MS; // 14 minutes

// Activity events that reset the idle timer
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];

// ---------------------------------------------------------------------------
// Styled components for the timeout warning overlay
// ---------------------------------------------------------------------------
const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  animation: ${fadeIn} 0.2s ease;
`;

const Modal = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 32px 40px;
  max-width: 420px;
  width: 90%;
  text-align: center;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
`;

const ModalTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 12px;
`;

const ModalBody = styled.p`
  font-size: 14px;
  color: #555;
  margin: 0 0 24px;
  line-height: 1.5;
`;

const ModalCountdown = styled.span`
  font-weight: 700;
  color: #cc0000;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
`;

const ModalBtn = styled.button`
  padding: 10px 24px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: opacity 0.15s;
  &:hover { opacity: 0.85; }
`;

const StayBtn = styled(ModalBtn)`
  background: #cc0000;
  color: #fff;
`;

const SignOutBtn = styled(ModalBtn)`
  background: #f0f0f0;
  color: #333;
`;

// Lightweight keepalive query — just checks viewer exists to touch last_active_at on the server
const keepaliveQuery = graphql`
  query MainAppBootstrapperKeepaliveQuery {
    viewer {
      id
    }
  }
`;

type Props = {
  children: *,
  alertNotifications: Array<Object>,
};

type State = {
  showWarning: boolean,
  countdown: number,
};

class MainAppBootstrapper extends PureComponent<Props, State> {
  postAuthInitiated = false;
  isAuthenticated = false;

  // Timers
  _warnTimer: ?TimeoutID = null;
  _logoutTimer: ?TimeoutID = null;
  _countdownInterval: ?IntervalID = null;

  state = {
    showWarning: false,
    countdown: Math.round(WARN_BEFORE_MS / 1000),
  };

  publicRoutes = ['/signin', '/sigin', '/signup'];

  isPublicRoute = () => {
    return this.publicRoutes.indexOf(window.location.pathname) > -1;
  };

  handlePostAuth = (viewer: Object) => {
    if (!this.postAuthInitiated) {
      this.postAuthInitiated = true;
      this.isAuthenticated = true;
      initAnalytics(viewer);
      this._startIdleTimer();
      ACTIVITY_EVENTS.forEach(evt =>
        window.addEventListener(evt, this._handleActivity, { passive: true }),
      );
    }
  };

  componentWillUnmount() {
    this._clearTimers();
    ACTIVITY_EVENTS.forEach(evt =>
      window.removeEventListener(evt, this._handleActivity),
    );
  }

  _clearTimers = () => {
    if (this._warnTimer)        clearTimeout(this._warnTimer);
    if (this._logoutTimer)      clearTimeout(this._logoutTimer);
    if (this._countdownInterval) clearInterval(this._countdownInterval);
    this._warnTimer = null;
    this._logoutTimer = null;
    this._countdownInterval = null;
  };

  _startIdleTimer = () => {
    this._clearTimers();
    this.setState({ showWarning: false, countdown: Math.round(WARN_BEFORE_MS / 1000) });

    this._warnTimer = setTimeout(() => {
      // Show the warning modal and start the countdown
      this.setState({ showWarning: true, countdown: Math.round(WARN_BEFORE_MS / 1000) });
      this._countdownInterval = setInterval(() => {
        this.setState(prev => {
          if (prev.countdown <= 1) return { countdown: 0 };
          return { countdown: prev.countdown - 1 };
        });
      }, 1000);
    }, WARN_AT_MS);

    this._logoutTimer = setTimeout(() => {
      window.location = '/signin?expired=true';
    }, IDLE_TIMEOUT_MS);
  };

  _handleActivity = () => {
    if (!this.isAuthenticated) return;
    if (this.state.showWarning) return; // user must explicitly click "Stay signed in"
    this._startIdleTimer();
  };

  _handleStaySignedIn = () => {
    // Touch the server so last_active_at gets updated, then reset the timer
    fetchQuery(relayEnvironment, keepaliveQuery, {}).toPromise().catch(() => {});
    this._startIdleTimer();
  };

  _handleSignOut = () => {
    window.location = '/signin';
  };

  render() {
    const { children, alertNotifications } = this.props;
    const { showWarning, countdown } = this.state;

    // NOTE: <Translation> is here to ensure the entire app re-renders upon language change.
    // This allows us to comfortably use the t() method without HOCs or render props
    return (
      <Suspense fallback={null}>
        <Translation>
          {t => (
            <>
              <QueryRenderer
                environment={relayEnvironment}
                dataFrom="STORE_THEN_NETWORK"
                variables={null}
                query={graphql`
                  query MainAppBootstrapperQuery {
                    viewer {
                      id
                      role
                      firstName
                      lastName
                      email
                      userNumber
                      organization {
                        name
                      }
                      company {
                        name
                        namespace
                        currency
                      }
                      updatedAt
                      createdAt
                    }
                  }
                `}
                render={({ error, props }) => {
                  if (error) {
                    return <p>{error.message}</p>;
                  }

                  if (!props) {
                    return null;
                  }

                  // If user isn't logged in and not viewing a public route, route to sign in page
                  if (!props.viewer && !this.isPublicRoute()) {
                    window.location = '/signin';
                    return null;
                  }

                  if (props.viewer) {
                    this.handlePostAuth(props.viewer);
                    if (props.viewer.company) {
                      setCompanyCurrency(props.viewer.company.currency);
                    }
                  }

                  // If user IS logged in and viewing the signin or signup page, route to home
                  if (
                    props.viewer &&
                    (window.location.pathname === '/signin' ||
                      window.location.pathname === '/sigin' ||
                      window.location.pathname === '/signup')
                  ) {
                    window.location = '/dashboard';
                    return null;
                  }

                  return (
                    <>
                      {children}
                      <AlertNotifications
                        alertNotifications={alertNotifications}
                      />
                      {config.env !== 'production' ? (
                        <div
                          style={{
                            position: 'fixed',
                            top: 8,
                            left: 16,
                            border: '1px solid #ccc',
                            background: 'rgba(255, 255, 255, 0.8)',
                            fontSize: 14,
                            fontWeight: 700,
                            color: '#f00',
                            padding: 5,
                            borderRadius: 5,
                            zIndex: 2,
                          }}
                        >
                          {`You're on the ${
                            window.location.href.indexOf('localhost') > -1
                              ? 'LOCAL'
                              : config.env.toUpperCase()
                          } app`}
                        </div>
                      ) : null}
                      {props.viewer ? <AiChat /> : null}
                    </>
                  );
                }}
              />

              {/* Session timeout warning modal */}
              {showWarning && (
                <Overlay>
                  <Modal>
                    <ModalTitle>Session Expiring Soon</ModalTitle>
                    <ModalBody>
                      You&apos;ve been inactive. For security, you&apos;ll be signed out in{' '}
                      <ModalCountdown>{countdown}</ModalCountdown>{' '}
                      {countdown === 1 ? 'second' : 'seconds'}.
                    </ModalBody>
                    <ModalActions>
                      <SignOutBtn onClick={this._handleSignOut}>Sign Out</SignOutBtn>
                      <StayBtn onClick={this._handleStaySignedIn}>Stay Signed In</StayBtn>
                    </ModalActions>
                  </Modal>
                </Overlay>
              )}
            </>
          )}
        </Translation>
      </Suspense>
    );
  }
}

// $FlowFixMe
export default withRouter(
  connect(state => ({
    alertNotifications: state.alertNotifications,
  }))(MainAppBootstrapper),
);

